/**
 * API client.
 *
 * Two things are deliberately absent compared to the previous version:
 *
 * 1. **No identity in request bodies.** The lock endpoints used to send `user_id` and
 *    `user_name`, which meant any caller could act as any clinician. The server now derives
 *    the actor from the verified token and ignores anything the body claims.
 * 2. **No invented user.** The app asks `GET /whoami` rather than holding a user it made up.
 */

import { authHeaders, AuthError, NetworkError, ServiceUnavailableError } from './auth';
import { authorizedFetch } from './cognito';
import {
  ConfirmationRate,
  Identity,
  StudyDetail,
  StudyLock,
  StudyPage,
  StudySummary,
  TargetClass,
} from './types';

const API_BASE = '/api/v1';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // authorizedFetch already tries a silent token refresh and retries once before this ever sees
  // a 401 — a 401 here means the refresh token itself is gone (or refresh failed), which is what
  // triggers cognito.ts's full-redirect fallback internally. Still throw, so an in-flight await
  // does not hang while that navigation completes.
  const res = await authorizedFetch(`${API_BASE}${path}`, init);

  if (res.status === 401) {
    throw new AuthError(401, await detail(res, 'Not signed in'));
  }
  if (res.status === 403) {
    throw new AuthError(403, await detail(res, 'Not permitted'));
  }
  if (res.status === 503) {
    throw new ServiceUnavailableError();
  }
  if (!res.ok) {
    throw new Error(await detail(res, `Request failed (${res.status})`));
  }
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

async function detail(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    return typeof body?.detail === 'string' ? body.detail : fallback;
  } catch {
    return fallback;
  }
}

// ── Identity ────────────────────────────────────────────────────────────────────

export function fetchIdentity(): Promise<Identity> {
  return request<Identity>('/whoami');
}

/**
 * The caller's own confirm/reject/reclassify rate per class (DESIGN.md §13.5 U15, §17.6 M3) —
 * "of the AFib findings I looked at, how many did I confirm", the surface a reviewer needs to
 * validate the engine from their own decisions rather than from a claim.
 */
export function fetchMyConfirmationRate(windowDays = 90): Promise<ConfirmationRate> {
  return request<ConfirmationRate>(`/me/confirmation-rate?window_days=${windowDays}`);
}

// ── Studies ─────────────────────────────────────────────────────────────────────

/**
 * One page of the worklist (DESIGN.md §13.6 R8).
 *
 * This used to return every study the caller could see, patient names included, and the app
 * refetched it every ten seconds — an unbounded query against a table holding every study the
 * platform has ever processed, and a standing transfer of the whole patient list to every open
 * tab. `total` comes back with the page so the UI can say "50 of 340" rather than reporting the
 * length of whatever arrived.
 */
export function fetchStudies(
  status?: string,
  limit = 50,
  offset = 0,
): Promise<StudyPage> {
  // The query parameter is `study_status`: `status` would shadow the response model's own
  // field name in the generated schema.
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status) params.set('study_status', status);
  return request<StudyPage>(`/studies?${params}`);
}

export function fetchStudyDetail(id: string): Promise<StudyDetail> {
  return request<StudyDetail>(`/studies/${id}`);
}

/**
 * Every finding the engine can report, and whether it can be switched off (D71, §6.12).
 *
 * Served by the API rather than compiled in: the catalogue is derived from the detector registry,
 * so a picker built from a hardcoded list would silently offer classes no detector emits, or omit
 * ones it does.
 */
export function fetchTargetClasses(): Promise<TargetClass[]> {
  return request<TargetClass[]>('/studies/analysis/target-classes');
}

// Signal fetching lives in signal.ts: it deals in binary and byte ranges, not JSON.

// ── Locks ───────────────────────────────────────────────────────────────────────
// No body at all: the holder is whoever the token says it is.

export function acquireLock(studyId: string): Promise<StudyLock> {
  return request<StudyLock>(`/studies/${studyId}/lock/acquire`, { method: 'POST' });
}

export function heartbeatLock(studyId: string): Promise<{ status: string }> {
  return request(`/studies/${studyId}/lock/heartbeat`, { method: 'POST' });
}

export function releaseLock(studyId: string): Promise<{ status: string }> {
  return request(`/studies/${studyId}/lock/release`, { method: 'POST' });
}

// Review actions live in review.ts: every one returns the refreshed workspace.

// ── Ingest (§13.6 I1/I2) ───────────────────────────────────────────────────────────

/**
 * Upload straight to storage rather than through this API (DESIGN.md §13.6 I1/I2, fixed
 * 2026-09-15): a real multi-day recording's byte transfer could never finish inside
 * `authorizedFetch`'s 20 s timeout or CloudFront's 60 s `/api/*` origin timeout, both of which
 * this flow now avoids entirely for the slow part. Three calls: presign (fast, JSON), the actual
 * bytes (slow, goes directly to storage, `XMLHttpRequest` so progress can be reported), complete
 * (fast, JSON — registers the study and hands off to the worker's async ingest).
 *
 * Above the backend's multipart threshold, the presign ticket splits the transfer into
 * concurrent parts instead of one stream (added 2026-09-16 — a real ~600 MB study measured at
 * 50s single-stream; concurrent parts use more of a connection's actual available bandwidth than
 * one TCP stream typically does).
 */
export interface UploadProgress {
  loaded: number;
  total: number;
}

interface UploadPart {
  part_number: number;
  url: string;
}

interface PresignResponse {
  study_id: string;
  method: 'PUT' | 'POST' | 'MULTIPART';
  url: string;
  fields: Record<string, string>;
  upload_id: string;
  parts: UploadPart[];
  part_size: number;
}

export async function uploadStudy(fields: {
  file: File;
  patientRef: string;
  patientName: string;
  patientGender?: string;
  patientAge?: number;
  studyTimezone?: string;
  /** Findings this study targets (D71). Omitted means "nobody chose" — the tenant's default,
   *  then the platform's, applies, and the study records which. */
  targetClasses?: string[];
  onProgress?: (p: UploadProgress) => void;
}): Promise<StudySummary> {
  const ticket = await request<PresignResponse>('/studies/upload/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ file_size: fields.file.size }),
  });

  let completedParts: { part_number: number; etag: string }[] | undefined;
  if (ticket.method === 'MULTIPART') {
    completedParts = await uploadMultipart(ticket, fields.file, fields.onProgress);
  } else {
    await uploadDirect(ticket, fields.file, fields.onProgress);
  }

  return request<StudySummary>('/studies/upload/complete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      study_id: ticket.study_id,
      patient_ref: fields.patientRef,
      patient_name: fields.patientName,
      patient_gender: fields.patientGender,
      patient_age: fields.patientAge,
      study_timezone: fields.studyTimezone ?? 'Asia/Kolkata',
      target_classes: fields.targetClasses ?? null,
      upload_id: ticket.method === 'MULTIPART' ? ticket.upload_id : undefined,
      parts: completedParts,
    }),
  });
}

/**
 * `XMLHttpRequest`, not `fetch`: it is the only thing in a browser that exposes upload progress
 * events, which a multi-minute direct-to-storage transfer needs (the old synchronous upload gave
 * none at all — a gap §13.6 I1 called out directly). Deliberately no client-side timeout — the
 * transfer's duration is the file size divided by the user's own connection, not a fixed budget,
 * and a fixed budget here is exactly the bug this flow exists to remove. Single stream only —
 * `uploadMultipart` below handles the "MULTIPART" ticket.
 */
function uploadDirect(ticket: PresignResponse, file: File, onProgress?: (p: UploadProgress) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(ticket.method, ticket.url, true);
    if (ticket.method === 'PUT') {
      // The local-dev-only passthrough (`storage.LocalSignalStore.presign_upload`) is a real,
      // auth-gated API endpoint, unlike an S3 presigned POST whose signature *is* the
      // authorization — so it needs the caller's own credentials attached by hand here, since
      // this deliberately bypasses `authorizedFetch` (and its 20 s timeout) for this request.
      Object.entries(authHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    }
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status}). Try again.`));
    };
    xhr.onerror = () => reject(new NetworkError());
    if (ticket.method === 'POST') {
      const form = new FormData();
      // Every field the presigned POST policy requires, then the file last — S3 requires the
      // file part to be the final one in a presigned POST's multipart body.
      Object.entries(ticket.fields).forEach(([k, v]) => form.append(k, v));
      form.append('file', file);
      xhr.send(form);
    } else {
      xhr.send(file);
    }
  });
}

//: How many parts upload at once. A browser's own per-origin connection cap (commonly 6) is the
//: real ceiling; this stays under it so the upload doesn't starve out other requests (the
//: portal's own polling, a second tab) sharing that budget.
const MULTIPART_CONCURRENCY = 4;

/**
 * Slice `file` into `ticket.parts.length` pieces of `ticket.part_size` bytes (the last one gets
 * the remainder) and PUT them concurrently, each to its own presigned URL. A bounded worker pool
 * over a shared cursor, not `Promise.all` on every part at once — with `MAX_UPLOAD_BYTES` (4 GiB)
 * at the backend's 32 MiB part size that would be up to 128 simultaneous requests, which the
 * concurrency cap above exists specifically to avoid.
 */
async function uploadMultipart(
  ticket: PresignResponse,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<{ part_number: number; etag: string }[]> {
  const total = file.size;
  const loadedByPart = new Array(ticket.parts.length).fill(0);
  const results: { part_number: number; etag: string }[] = new Array(ticket.parts.length);

  const report = () => {
    if (!onProgress) return;
    onProgress({ loaded: loadedByPart.reduce((a, b) => a + b, 0), total });
  };

  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < ticket.parts.length) {
      const i = nextIndex++;
      const part = ticket.parts[i];
      const start = i * ticket.part_size;
      const end = Math.min(start + ticket.part_size, total);
      const etag = await uploadPart(part.url, file.slice(start, end), (loaded) => {
        loadedByPart[i] = loaded;
        report();
      });
      results[i] = { part_number: part.part_number, etag };
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(MULTIPART_CONCURRENCY, ticket.parts.length) }, worker),
  );
  return results;
}

function uploadPart(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Part upload failed (${xhr.status}). Try again.`));
        return;
      }
      // Requires the signal bucket's CORS to expose ETag (scribe-infra `modules/storage`) — a
      // cross-origin response hides every header except what `expose_headers` lists.
      const etag = xhr.getResponseHeader('ETag');
      if (!etag) {
        reject(new Error('S3 did not return an ETag for this part.'));
        return;
      }
      resolve(etag);
    };
    xhr.onerror = () => reject(new NetworkError());
    xhr.send(blob);
  });
}
